import { useState } from "react";
import ReferenceLink from "../EmissionFactorBreakdown/ReferenceLink";
import SourceForm from "./SourceForm";
import { deleteEnergySource } from "./energyApi";
import { draftFromSource, emptySourceDraft } from "./helpers";

/**
 * The energy sources, latest first, with their factor counts and source
 * links, a form to add or edit one, and delete for a source that holds no
 * factors.
 *
 * @param {object} props
 * @param {{ rows: import("./helpers").EnergySource[], loading: boolean, error: string, reload: () => Promise<void> }} props.sources
 * @param {import("../EmissionFactorBreakdown/referencesApi").GhgReference[]} props.references
 * @param {() => void} props.onReferencesChanged
 * @param {() => void} props.onSourcesChanged refreshes what shows source names, such as the factor list
 */
export default function SourcesTab({ sources, references, onReferencesChanged, onSourcesChanged }) {
  const [formDraft, setFormDraft] = useState(null);
  const [notice, setNotice] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /** @param {import("./helpers").SourceDraft} draft */
  const openForm = (draft) => {
    setNotice(null);
    setFormDraft(draft);
  };

  /** @param {import("./helpers").EnergySource} saved */
  const afterSave = (saved) => {
    setFormDraft(null);
    setNotice({ text: `${saved.name} saved.`, failed: false });
    sources.reload();
    onSourcesChanged();
    onReferencesChanged();
  };

  /** @param {import("./helpers").EnergySource} source */
  const remove = async (source) => {
    if (!window.confirm(`Delete the energy source "${source.name}"?`)) return;
    setNotice(null);
    setDeletingId(source.id);
    const res = await deleteEnergySource(source.id);
    setDeletingId(null);
    if (!res?.isSuccess) {
      setNotice({ text: res?.data?.error || `Couldn't delete ${source.name}.`, failed: true });
      return;
    }
    setNotice({ text: `${source.name} deleted.`, failed: false });
    sources.reload();
    onReferencesChanged();
  };

  return (
    <div className="gdb-root" role="tabpanel" id="enf-panel-sources" aria-labelledby="enf-tab-sources">
      <div className="enf-tabbar-actions">
        <p className="gdb-lead">The sets of energy factors companies are priced from.</p>
        {formDraft ? null : (
          <button type="button" className="kgb-btn kgb-btn--primary" onClick={() => openForm(emptySourceDraft())}>
            + Add source
          </button>
        )}
      </div>

      {formDraft ? (
        <SourceForm
          key={formDraft.id ?? "new"}
          initial={formDraft}
          sources={sources.rows}
          references={references}
          onReferencesChanged={onReferencesChanged}
          onSaved={afterSave}
          onCancel={() => setFormDraft(null)}
        />
      ) : null}

      {notice ? (
        <p className={`gdb-notice${notice.failed ? " gdb-notice--error" : ""}`} role={notice.failed ? "alert" : "status"}>
          {notice.text}
        </p>
      ) : null}

      {sources.error ? (
        <div className="gdb-message gdb-message--error">
          {sources.error}{" "}
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={sources.reload}>
            Retry
          </button>
        </div>
      ) : null}
      {!sources.error && sources.loading ? <div className="gdb-message">Loading energy sources…</div> : null}
      {!sources.error && !sources.loading && !sources.rows.length ? <div className="gdb-message">No energy sources yet.</div> : null}

      {sources.rows.length ? (
        <div className="kgb-card gdb-table-wrap">
          <table className="gdb-table">
            <thead>
              <tr>
                <th scope="col" className="gdb-num">
                  Priority
                </th>
                <th scope="col">Source</th>
                <th scope="col" className="gdb-num">
                  Factors
                </th>
                <th scope="col">Source link</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sources.rows.map((source) => (
                <tr key={source.id} className={formDraft?.id === source.id ? "gdb-row--editing" : undefined}>
                  <td className="gdb-num">
                    <span className="gdb-priority">{source.priority_order}</span>
                  </td>
                  <td>
                    <div className="gdb-name">{source.name}</div>
                    {source.source && source.source !== source.name ? <div className="gdb-muted">{source.source}</div> : null}
                  </td>
                  <td className="gdb-num">{source.factor_count.toLocaleString("en-IN")}</td>
                  <td>
                    {source.reference ? <ReferenceLink reference={source.reference} /> : <span className="gdb-missing">No link</span>}
                  </td>
                  <td className="gdb-actions">
                    <button
                      type="button"
                      className="kgb-btn"
                      aria-label={`Edit ${source.name}`}
                      onClick={() => openForm(draftFromSource(source))}
                    >
                      Edit
                    </button>{" "}
                    <button
                      type="button"
                      className="kgb-btn kgb-btn--quiet kgb-btn--danger"
                      aria-label={`Delete ${source.name}`}
                      disabled={source.factor_count > 0 || deletingId === source.id}
                      title={source.factor_count > 0 ? "Delete or move its factors first" : undefined}
                      onClick={() => remove(source)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
